/**
 * Valuation view (batch 8 T14c, #3036). Financial only.
 *
 * "Complete" from the server means the ledger could RECONSTRUCT every stock key, not that every cost is known: a value
 * with units of unconfirmed cost, keys the ledger could not rebuild, or stock with no cost layer is shown as a PARTIAL
 * known value with those counts beside it. A group whose every key is unavailable is unknown, never 0.
 */
import { Chip, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WireValuation, WireValuationGroup } from '@/hooks/useInventoryReports';
import { Figure, MetricValue, Money, Named, PartialWarning, ReconciledTotal, ReportTable, SourceNote, Stat, Unknown, WindowInfoText, useReportDisplay } from './ReportParts';
import { formatInstant } from './reportFormat';
import { reasonKey } from './reportLabels';

/** Whether a valuation total (or one group) is only the known part of the value. Pure: the server's counts, no arithmetic on money. */
export function isPartialValuation(x: {
  complete: boolean;
  unconfirmedQuantity: number;
  unavailableKeys: number;
  unbackedQuantity: WireValuation['totals']['unbackedQuantity'];
}): boolean {
  return !x.complete || x.unconfirmedQuantity > 0 || x.unavailableKeys > 0 || (x.unbackedQuantity.available && x.unbackedQuantity.value > 0);
}

/** A group with no available key has no value at all: the server's `"0.0000"` there is the absence of one. */
export const hasNoValue = (g: { availableKeys: number; unavailableKeys: number }): boolean => g.availableKeys === 0 && g.unavailableKeys > 0;

export function ValuationView({ data }: { data: WireValuation }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const zone = data.asOfRequest.timeZone;
  const totals = data.totals;
  const partial = isPartialValuation(totals);
  const noValue = hasNoValue(totals);
  return (
    <Stack spacing={2}>
      <WindowInfoText>
        {t('valuation.asOf', { at: formatInstant(data.asOf, zone, language) })}{' '}
        {t(`valuation.kind.${data.asOfKind}`)} {t(`valuation.interpretation.${data.asOfRequest.interpretation}`, { input: data.asOfRequest.input ?? '', zone })}
      </WindowInfoText>
      {data.earliestBaselineAt !== null && (
        <Typography variant="caption" color="text.secondary">
          {t('valuation.baseline', { at: formatInstant(data.earliestBaselineAt, zone, language) })}
        </Typography>
      )}
      <SourceNote source={data.source} />
      {partial && (
        <PartialWarning>
          {t('valuation.partial', {
            unconfirmed: totals.unconfirmedQuantity,
            keys: totals.unavailableKeys,
            unbacked: totals.unbackedQuantity.available ? totals.unbackedQuantity.value : 0,
          })}
        </PartialWarning>
      )}
      {!totals.unbackedQuantity.available && (
        <Typography variant="caption" color="text.secondary">
          {t('valuation.unbackedUnknown', { reason: t(reasonKey(totals.unbackedQuantity.reason)) })}
        </Typography>
      )}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {noValue ? (
          <Stat label={t('valuation.knownValue')}>
            <Unknown reason="no_layers" />
          </Stat>
        ) : (
          <ReconciledTotal label={t(partial ? 'valuation.knownValuePartial' : 'valuation.knownValue')} total={totals} />
        )}
        <Stat label={t('valuation.knownQuantity')}>
          <Figure value={totals.knownQuantity} />
        </Stat>
        <Stat label={t('valuation.unconfirmedQuantity')}>
          <Figure value={totals.unconfirmedQuantity} />
        </Stat>
        <Stat label={t('valuation.unbackedQuantity')}>
          <MetricValue metric={totals.unbackedQuantity} render={(v) => <Figure value={v} />} />
        </Stat>
        <Stat label={t('valuation.keys')} caption={t('valuation.keysCaption', { available: totals.availableKeys, unavailable: totals.unavailableKeys })}>
          {partial ? <Chip size="small" color="warning" label={t('partial.label')} /> : <Chip size="small" color="success" label={t('valuation.reconstructed')} />}
        </Stat>
      </Stack>
      <ReportTable label={t('valuation.label')}>
        <TableHead>
          <TableRow>
            <TableCell>{t(`valuation.group.${data.groupBy}`)}</TableCell>
            <TableCell align="right">{t('valuation.knownValue')}</TableCell>
            <TableCell align="right">{t('valuation.knownQuantity')}</TableCell>
            <TableCell align="right">{t('valuation.unconfirmedQuantity')}</TableCell>
            <TableCell align="right">{t('valuation.unbackedQuantity')}</TableCell>
            <TableCell>{t('valuation.unavailable')}</TableCell>
            <TableCell>{t('valuation.state')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.groups.map((g) => (
            <GroupRow key={g.key} group={g} groupBy={data.groupBy} />
          ))}
        </TableBody>
      </ReportTable>
    </Stack>
  );
}

function GroupRow({ group: g, groupBy }: { group: WireValuationGroup; groupBy: WireValuation['groupBy'] }) {
  const { t } = useTranslation('inventoryReports');
  const groupPartial = isPartialValuation(g);
  const reasons = Object.entries(g.unavailableReasons);
  return (
    <TableRow>
      <TableCell><Named kind={groupBy} id={g.key} /></TableCell>
      <TableCell align="right">
        {hasNoValue(g) ? <Unknown reason={reasons[0]?.[0] ?? 'no_layers'} /> : <Money cents4={g.valueCents4} cents={g.valueCents} />}
      </TableCell>
      <TableCell align="right"><Figure value={g.knownQuantity} /></TableCell>
      <TableCell align="right"><Figure value={g.unconfirmedQuantity} /></TableCell>
      <TableCell align="right"><MetricValue metric={g.unbackedQuantity} render={(v) => <Figure value={v} />} /></TableCell>
      <TableCell>
        {reasons.length === 0
          ? ''
          : reasons.map(([reason, count]) => (
              <Typography key={reason} variant="caption" component="div">
                {t(reasonKey(reason))}: <Figure value={count} />
              </Typography>
            ))}
      </TableCell>
      <TableCell>{groupPartial ? <Chip size="small" color="warning" variant="outlined" label={t('partial.label')} /> : <Chip size="small" color="success" variant="outlined" label={t('valuation.reconstructed')} />}</TableCell>
    </TableRow>
  );
}
